import { Injectable, signal } from '@angular/core';

const FORWARD_SECONDS = 30;
const BACKWARD_SECONDS = 10;

@Injectable({ providedIn: 'root'})

export class VideoPlayerService {
    public isPlaying = signal(false);
    public isMuted = signal(false);
    public currentTime = signal(0);
    public duration = signal(0);
    public durationString = signal<string | null>(null);
    public playingDurationString = signal('00:00');
    public error = signal<string | null>(null);

    public audio = signal<HTMLAudioElement | undefined>(undefined);
    private listeners?: AbortController;

    private onLoadedMetadata = () => {
        if (!this.audio()) 
            return;

        const duration = this.audio().duration || 0;
        this.duration.set(duration);
        this.durationString.set(this.formatTime(duration));
    };
    private onTimeUpdate = () => this.updateProgress();
    private onCanPlay = () => this.error.set(null);
    private onError = () => {
        this.error.set('No se puede cargar el audio. Revisa la fuente.');
        this.isPlaying.set(false);
        this.teardownAudio();
    };
    private onEnded = () => {
        this.isPlaying.set(false);
        this.currentTime.set(0);
        this.playingDurationString.set('00:00');
    };

    public loadTrack(url: string): void {
        this.teardownAudio();

        this.audio.set( new Audio(url) );
        this.audio().preload = 'metadata';
        this.audio().muted = this.isMuted();

        this.listeners = new AbortController();
        const opt = { signal: this.listeners.signal } as AddEventListenerOptions;

        this.audio().addEventListener('loadedmetadata', this.onLoadedMetadata, opt);
        this.audio().addEventListener('timeupdate', this.onTimeUpdate, opt);
        this.audio().addEventListener('canplay', this.onCanPlay, opt);
        this.audio().addEventListener('error', this.onError, opt);
        this.audio().addEventListener('ended', this.onEnded, opt);
    }

    public teardownAudio(): void {
        this.listeners?.abort();
        this.listeners = undefined;
        if (this.audio()) {
            try { this.audio().pause() } catch {}
            this.audio().src = '';
            try { this.audio().load() } catch {}
        }
        
        this.audio.set(undefined);
        this.resetData();
    }

    public handleForward(): void {
        if (!this.audio()) 
            return;

        const expected = this.currentTime() + FORWARD_SECONDS;
        const clamped = this.clamp(expected, 0, this.duration() || 0);
        this.audio().currentTime = clamped;
        if (this.isPlaying()) 
            this.audio().play();
    }

    public handleBackward(): void {
        if (!this.audio()) 
            return;

        const expected = this.currentTime() - BACKWARD_SECONDS;
        const clamped = this.clamp(expected, 0, this.duration() || 0);
        this.audio().currentTime = clamped;
        if (this.isPlaying()) 
            this.audio().play();
    }

    public async handlePlayPause(): Promise<void> {
        if (!this.audio()) 
            return;

        if (this.isPlaying()) {
            this.audio().pause();
            this.isPlaying.set(false);
            return;
        }

        try {
            await this.audio().play();
            this.isPlaying.set(true);
        } catch {
            this.error.set('No se pudo iniciar la reproducción. Inténtalo de nuevo.');
            this.isPlaying.set(false);
        }
    }

    public async playAudio(){
        if (!this.audio())
            return;

         await this.audio().play();
         this.isPlaying.set(true);
    }

    public stopAudio(){
        if (!this.audio())
            return;

        this.audio().pause();
        this.isPlaying.set(false);
    }

    public toggleMute(): void {
        if (!this.audio()) 
            return;

        this.isMuted.update((prev) => !prev);
        this.audio().muted = this.isMuted();
    }

    public changeValue(value: number | null): void {
        if (!this.audio() || value == null) 
            return;

        const clamped = this.clamp(value, 0, this.duration() || 0);
        this.audio().currentTime = clamped;
        if (this.isPlaying()) 
            this.audio().play();
    }

    private clamp(v: number, min: number, max: number): number {
        return Math.min(max, Math.max(min, v));
    }

    private formatTime(seconds: number): string {
        if (!seconds || !isFinite(seconds) || isNaN(seconds)) 
            return '00:00';

        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        const mm = mins < 10 ? '0' + mins : String(mins);
        const ss = secs < 10 ? '0' + secs : String(secs);

        if (hrs > 0) {
            const hh = hrs < 10 ? '0' + hrs : String(hrs);
            return `${hh}:${mm}:${ss}`;
        }

        return `${mm}:${ss}`;
    }

    private updateProgress(): void {
        if (!this.audio()) 
            return;

        const timeElapsed = this.audio().currentTime || 0;
        this.currentTime.set(timeElapsed);
        this.playingDurationString.set(this.formatTime(timeElapsed));
        if (!this.duration() && this.audio().duration && isFinite(this.audio().duration)) {
            const duration = this.audio().duration;
            this.duration.set(duration);
            this.durationString.set(this.formatTime(duration));
        }
    }

    private resetData(){
        this.isPlaying.set(false);
        this.isMuted.set(false);
        this.currentTime.set(0);
        this.duration.set(0);
        this.error.set(null);
        this.durationString.set(null);
        this.playingDurationString.set('00:00')
    }

}